// Fleet Platform Management API — Phase 4
// Feature-flagged: runtime-config envelope `featureFlags.useMockPlatformApi`
//   true  → in-memory mocks
//   false → real /ocnode/* endpoints (default)
//
// Was previously `VITE_USE_MOCK_PLATFORM_API`; migrated to runtime envelope
// (sub-card #C, MMM) so a deploy can flip the flag without a rebuild. Once
// sub-card #E ships hot-reload, flipping the DDB flag propagates within
// seconds without a page reload.
//
// Swap to real endpoints: flip the flag + ensure backend Phases 1-3 are live.

// #2542 — import getFleetHttpUrl() (lazy) instead of stale module-level constant.
import { getFleetHttpUrl } from "../lib/fleetRoles";
import { getRuntimeConfig } from "../types/RuntimeConfig";
import { fleetApi } from "./fleetApi";

function useMockPlatformApi(): boolean {
  return Boolean(getRuntimeConfig()?.featureFlags?.useMockPlatformApi);
}

// ── Types ─────────────────────────────────────────────────────────────────────

export type NodeStatus = "pending" | "active" | "revoked" | "offline";
export type UserStatus = "active" | "suspended" | "deleted";
export type AgentStatus = "running" | "stopped" | "error";
export type AgentTier = "core" | "ondemand";
export type ClaudeMdPolicy = "db-generated" | "hand-edited";
export type ModelName = "opus" | "sonnet" | "haiku";

export interface OcNode {
  node_id:       string;
  display_name:  string;
  hostname:      string;
  status:        NodeStatus;
  capabilities:  string[];
  owner_user_id: string | null;
  last_seen:     string | null;
  connected_at:  string | null;
  version:       string;
}

export interface PlatformUser {
  id:              string;   // linux username e.g. "u_gergo"
  display_name:    string;
  email:           string;
  status:          UserStatus;
  ssh_key_present: boolean;
  session_count:   number;
  created_at:      string;
}

export interface Project {
  id:               number;
  user_id:          string;
  name:             string;
  description:      string | null;
  host:             string;        // node_id
  path:             string;
  claude_md_policy: ClaudeMdPolicy;
  created_at:       string;
}

export interface Agent {
  id:         number;
  project_id: number;
  session:    string;
  model:      ModelName;
  tier:       AgentTier;
  host:       string;
  role:       string | null;
  status:     AgentStatus;
  created_at: string;
}

export interface AuditEntry {
  id:          number;
  node_id:     string;
  user_id:     string | null;
  op:          string;
  args:        Record<string, unknown> | null;
  result_ok:   boolean;
  duration_ms: number;
  ts:          string;
}

export interface AuditResult {
  entries: AuditEntry[];
  total:   number;
  limit:   number;
  offset:  number;
}

export interface ProvisionParams {
  display_name: string;
  email:        string;
  ssh_pubkey:   string;
  claude_key:   string;
  gpg_mode:     "skip" | "upload" | "generate";
  gpg_key?:     string;
  git_name:     string;
  git_email:    string;
}

export interface AuthPendingJob {
  id:   number;
  user: string;
  url:  string;
}

// ── HTTP helpers ──────────────────────────────────────────────────────────────

function getToken(): string | null { return localStorage.getItem("jwt"); }

async function apiGet<T>(path: string, params?: Record<string, string>): Promise<T> {
  const qs = params ? "?" + new URLSearchParams(params).toString() : "";
  const res = await fetch(`${getFleetHttpUrl()}${path}${qs}`, {
    headers: { Authorization: `Bearer ${getToken() ?? ""}` },
  });
  if (!res.ok) {
    const b = await res.json().catch(() => ({}));
    throw new Error((b as { error?: string }).error ?? `Platform API ${res.status}: ${path}`);
  }
  return res.json();
}

async function apiPost<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${getFleetHttpUrl()}${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getToken() ?? ""}`,
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const b = await res.json().catch(() => ({}));
    throw new Error((b as { error?: string }).error ?? `Platform API ${res.status}: ${path}`);
  }
  return res.json();
}

async function apiDelete(path: string): Promise<void> {
  const res = await fetch(`${getFleetHttpUrl()}${path}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${getToken() ?? ""}` },
  });
  if (!res.ok) {
    const b = await res.json().catch(() => ({}));
    throw new Error((b as { error?: string }).error ?? `Platform API ${res.status}: ${path}`);
  }
}

// ── Mock state (in-memory, resets on page reload) ─────────────────────────────

let _mockUsers: PlatformUser[] = [
  {
    id: "u_alice", display_name: "Alice Example", email: "alice@example.com",
    status: "active", ssh_key_present: true, session_count: 2,
    created_at: new Date(Date.now() - 86_400_000 * 2).toISOString(),
  },
];

let _mockProjects: Project[] = [
  {
    id: 1, user_id: "u_alice", name: "default", description: null,
    host: "n-hetzner-01", path: "/home/u_alice/projects/default",
    claude_md_policy: "db-generated", created_at: new Date(Date.now() - 86_400_000 * 2).toISOString(),
  },
];

let _mockAgents: Agent[] = [
  {
    id: 1, project_id: 1, session: "default",   model: "sonnet", tier: "ondemand", host: "n-hetzner-01", role: null,                  status: "stopped",  created_at: new Date(Date.now() - 86_400_000 * 2).toISOString(),
  },
];

let _mockAuditLog: AuditEntry[] = Array.from({ length: 50 }, (_, i) => {
  const ops = ["provision_user","write_authorized_keys","list_sessions","kill_session",
                "send_claude_input","set_claude_token","create_dir","write_claude_md"];
  const users = ["u_alice", null];
  const ok = i % 7 !== 0;
  return {
    id:          50 - i,
    node_id:     i % 3 === 2 ? "n-jamesave-01" : "n-hetzner-01",
    user_id:     users[i % 3],
    op:          ops[i % ops.length],
    args:        null,
    result_ok:   ok,
    duration_ms: 20 + (i * 13) % 800,
    ts:          new Date(Date.now() - i * 3_600_000).toISOString(),
  };
});

let _mockIdSeq = 10;

// ── Mock helper ───────────────────────────────────────────────────────────────

function delay(ms = 200): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

// ── Exported API ──────────────────────────────────────────────────────────────

export const platformApi = {

  // ── Users ──────────────────────────────────────────────────────────────────

  listUsers: async (): Promise<PlatformUser[]> => {
    if (useMockPlatformApi()) { await delay(); return [..._mockUsers]; }
    const r = await apiGet<PlatformUser[] | { users?: unknown[] }>("/platform/users");
    const rows: unknown[] = Array.isArray(r) ? r : ((r as { users?: unknown[] }).users ?? []);
    return rows.map((row: unknown) => {
      const u = row as Record<string, unknown>;
      return {
        id:              (u.id ?? u.user_id ?? "") as string,
        display_name:    (u.display_name ?? u.user_id ?? u.id ?? "") as string,
        email:           (u.email ?? "") as string,
        status:          (u.status ?? "active") as PlatformUser["status"],
        ssh_key_present: Boolean(u.ssh_key_present ?? false),
        session_count:   Number(u.session_count ?? u.agent_count ?? 0),
        created_at:      (u.created_at ?? "") as string,
      };
    });
  },

  provisionUser: async (params: ProvisionParams): Promise<PlatformUser> => {
    if (useMockPlatformApi()) {
      await delay(800);
      const id = `u_${params.display_name.toLowerCase().split(/\s+/)[0]}${_mockIdSeq++}`;
      const user: PlatformUser = {
        id, display_name: params.display_name, email: params.email,
        status: "active", ssh_key_present: true, session_count: 0,
        created_at: new Date().toISOString(),
      };
      _mockUsers.push(user);
      return user;
    }
    return apiPost<PlatformUser>("/platform/users", params);
  },

  deleteUser: async (user_id: string): Promise<void> => {
    if (useMockPlatformApi()) {
      await delay(400);
      _mockUsers = _mockUsers.filter((u) => u.id !== user_id);
      _mockProjects = _mockProjects.filter((p) => p.user_id !== user_id);
      _mockAgents = _mockAgents.filter((a) => !_mockProjects.some((p) => p.id === a.project_id && p.user_id === user_id));
      return;
    }
    await apiDelete(`/platform/users/${user_id}`);
  },

  rotateSSHKey: async (_user_id: string, _ssh_pubkey: string): Promise<void> => {
    if (useMockPlatformApi()) { await delay(300); return; }
    // Not yet deployed — no-op; use dispatch to rotate keys out-of-band
  },

  rotateToken: async (_user_id: string): Promise<{ token: string }> => {
    if (useMockPlatformApi()) { await delay(200); return { token: `mock-token-${Date.now()}` }; }
    // Not yet deployed — no-op
    return { token: "" };
  },

  // ── Projects ───────────────────────────────────────────────────────────────

  listProjects: async (user_id?: string): Promise<Project[]> => {
    if (useMockPlatformApi()) {
      await delay();
      return user_id ? _mockProjects.filter((p) => p.user_id === user_id) : [..._mockProjects];
    }
    return apiGet<Project[]>("/platform/projects", user_id ? { user_id } : undefined);
  },

  createProject: async (params: { user_id: string; name: string; description?: string; host: string }): Promise<Project> => {
    if (useMockPlatformApi()) {
      await delay(400);
      const proj: Project = {
        id: _mockIdSeq++, user_id: params.user_id, name: params.name,
        description: params.description ?? null, host: params.host,
        path: `/home/${params.user_id}/projects/${params.name}`,
        claude_md_policy: "db-generated", created_at: new Date().toISOString(),
      };
      _mockProjects.push(proj);
      return proj;
    }
    return apiPost("/platform/projects", params);
  },

  deleteProject: async (project_id: number): Promise<void> => {
    if (useMockPlatformApi()) {
      await delay(300);
      _mockProjects = _mockProjects.filter((p) => p.id !== project_id);
      _mockAgents   = _mockAgents.filter((a) => a.project_id !== project_id);
      return;
    }
    await apiDelete(`/platform/projects/${project_id}`);
  },

  // ── Agents ─────────────────────────────────────────────────────────────────

  listAgents: async (project_id?: number): Promise<Agent[]> => {
    if (useMockPlatformApi()) {
      await delay();
      return project_id ? _mockAgents.filter((a) => a.project_id === project_id) : [..._mockAgents];
    }
    if (project_id !== undefined) {
      const body = await apiGet<{ project: Project; agents: Agent[] }>(`/platform/projects/${project_id}`);
      return body.agents ?? [];
    }
    // No bulk-agents endpoint — fetch all projects then pull agents per project
    const projects = await apiGet<Project[]>("/platform/projects");
    const chunks = await Promise.all(
      projects.map((p) =>
        apiGet<{ project: Project; agents: Agent[] }>(`/platform/projects/${p.id}`)
          .then((b) => b.agents ?? [])
          .catch(() => [] as Agent[])
      )
    );
    return chunks.flat();
  },

  addAgent: async (params: { project_id: number; session: string; model: ModelName; tier: AgentTier; host: string; role?: string }): Promise<Agent> => {
    if (useMockPlatformApi()) {
      await delay(300);
      const agent: Agent = {
        id: _mockIdSeq++, project_id: params.project_id, session: params.session,
        model: params.model, tier: params.tier, host: params.host,
        role: params.role ?? null, status: "stopped", created_at: new Date().toISOString(),
      };
      _mockAgents.push(agent);
      return agent;
    }
    return apiPost<Agent>(`/platform/projects/${params.project_id}/agents`, {
      session: params.session, model: params.model, tier: params.tier,
      host: params.host, role: params.role,
    });
  },

  startAgent: async (agent_id: number): Promise<void> => {
    if (useMockPlatformApi()) {
      await delay(600);
      const a = _mockAgents.find((x) => x.id === agent_id);
      if (a) a.status = "running";
      return;
    }
    // Not yet deployed — optimistic UI update only
  },

  stopAgent: async (agent_id: number): Promise<void> => {
    if (useMockPlatformApi()) {
      await delay(400);
      const a = _mockAgents.find((x) => x.id === agent_id);
      if (a) a.status = "stopped";
      return;
    }
    // Not yet deployed — optimistic UI update only
  },

  // project_id + session required (backend key is project/session, not agent id)
  removeAgent: async (project_id: number, session: string): Promise<void> => {
    if (useMockPlatformApi()) {
      await delay(200);
      _mockAgents = _mockAgents.filter((a) => !(a.project_id === project_id && a.session === session));
      return;
    }
    await apiDelete(`/platform/projects/${project_id}/agents/${encodeURIComponent(session)}`);
  },

  // ── Audit log ──────────────────────────────────────────────────────────────

  listAuditLog: async (params?: { user_id?: string; node_id?: string; op?: string; limit?: number; offset?: number }): Promise<AuditResult> => {
    if (useMockPlatformApi()) {
      await delay();
      let entries = [..._mockAuditLog];
      if (params?.user_id) entries = entries.filter((e) => e.user_id === params.user_id);
      if (params?.node_id) entries = entries.filter((e) => e.node_id === params.node_id);
      if (params?.op)      entries = entries.filter((e) => e.op.includes(params.op!));
      const total  = entries.length;
      const limit  = params?.limit  ?? 25;
      const offset = params?.offset ?? 0;
      return { entries: entries.slice(offset, offset + limit), total, limit, offset };
    }
    const qp: Record<string, string> = {};
    if (params?.user_id) qp.user_id = params.user_id;
    if (params?.node_id) qp.node_id = params.node_id;
    if (params?.op)      qp.op      = params.op;
    if (params?.limit)   qp.limit   = String(params.limit);
    if (params?.offset)  qp.offset  = String(params.offset);
    return apiGet<AuditResult>("/ocnode/audit", Object.keys(qp).length ? qp : undefined);
  },

  // ── Claude device-flow auth (§5) ───────────────────────────────────────────

  listAuthJobs: async (): Promise<AuthPendingJob[]> => {
    if (useMockPlatformApi()) return [];
    try {
      const jobs = await fleetApi.listJobs({ target: "frontend", status: "sent" });
      return jobs
        .filter((j) => j.title.startsWith("AUTH_REQUIRED:"))
        .map((j) => {
          const parts = j.title.split(":");
          return { id: j.id, user: parts[1] ?? "", url: parts.slice(2).join(":") };
        });
    } catch { return []; }
  },

  submitAuthToken: async (job_id: number, user: string, token: string): Promise<void> => {
    if (useMockPlatformApi()) { await delay(300); return; }
    await apiPost("/ocnode/auth-token", { job_id, user, token });
  },
};
